import React, { useEffect, useCallback, useState } from "react";
import { ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { useLang } from "@/context/LangContext";

export const Specialties = () => {
  const { t } = useLang();
  const s = t.specialties;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    skipSnaps: false,
    dragFree: true,
  });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  // Autoplay
  useEffect(() => {
    if (!emblaApi) return;
    const id = setInterval(() => {
      if (!emblaApi) return;
      emblaApi.scrollNext();
    }, 3200);
    return () => clearInterval(id);
  }, [emblaApi]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  return (
    <section
      id="specialties"
      className="relative py-24 md:py-32 bg-white overflow-hidden"
      data-testid="specialties-section"
      aria-label={s.title}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 max-w-5xl">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-green-700">
              <MapPin className="h-3.5 w-3.5" /> {s.eyebrow}
            </span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
              {s.title}
            </h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">{s.desc}</p>
          </div>

          <div className="flex gap-2 md:mb-2" data-testid="specialties-controls">
            <button
              type="button"
              onClick={scrollPrev}
              disabled={!canScrollPrev}
              aria-label="Previous"
              className="h-11 w-11 rounded-full border border-slate-200 bg-white text-slate-700 hover:border-green-400 hover:text-green-700 inline-flex items-center justify-center transition-colors disabled:opacity-40"
              data-testid="specialties-prev"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={scrollNext}
              disabled={!canScrollNext}
              aria-label="Next"
              className="h-11 w-11 rounded-full border border-slate-200 bg-white text-slate-700 hover:border-green-400 hover:text-green-700 inline-flex items-center justify-center transition-colors disabled:opacity-40"
              data-testid="specialties-next"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="mt-12 relative">
        <div className="absolute inset-y-0 left-0 w-16 md:w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-16 md:w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

        <div className="embla overflow-hidden" ref={emblaRef} data-testid="specialties-carousel">
          <div className="flex gap-5 px-6 md:px-12">
            {s.items.map((item, i) => (
              <article
                key={item.name || i}
                className="group relative flex-none w-[260px] sm:w-[280px] md:w-[300px] h-[380px] rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-1 bg-slate-200"
                data-testid={`specialty-card-${i}`}
              >
                <img
                  src={item.img}
                  alt={item.name}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://images.pexels.com/photos/4506217/pexels-photo-4506217.jpeg?auto=compress&w=800";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <span className="inline-block rounded-full bg-white/10 backdrop-blur-md border border-white/20 px-3 py-1 text-[10px] uppercase tracking-widest font-bold text-white">
                    {String(i + 1).padStart(2, "0")} / {String(s.items.length).padStart(2, "0")}
                  </span>
                  <h3 className="mt-3 font-display text-xl font-bold text-white leading-tight tracking-tight">
                    {item.name}
                  </h3>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Specialties;
